import { useEffect, useState } from 'react';
import AjudaTela from '../../components/AjudaTela';
import TelaPermissoesGrid from '../../components/TelaPermissoesGrid';
import { api } from '../../services/api';

const VERDE = '#1a3d2a';
const AZUL = '#1e40af';

const AJUDA = {
  titulo: 'Usuários e Permissões',
  descricao:
    'Cadastre aqui os colaboradores que vão acessar o sistema e controle exatamente o que cada um pode ver ou fazer, tela por tela. Essa tela é só para administração de acesso — não confunda com o cadastro de Funcionários (usado em Atividades/Máquinas), que é outra coisa.',
  secoes: [
    {
      titulo: 'Novo Colaborador',
      campos: [
        { nome: 'Nome Completo', obrigatorio: true, explicacao: 'Nome que vai identificar o colaborador nas listas do sistema.' },
        { nome: 'E-mail', obrigatorio: true, explicacao: 'Usado para identificar a conta. O sistema gera um usuário e uma senha temporária automaticamente ao criar o colaborador.' },
        { nome: 'Perfil', obrigatorio: false, explicacao: 'Se escolher um perfil aqui, as permissões dele já são aplicadas na hora da criação. Deixe em branco se quiser montar as permissões manualmente depois, ou use "Duplicar de outro colaborador".' },
      ],
    },
    {
      titulo: 'Permissões por Tela',
      campos: [
        { nome: 'Caixa de seleção do módulo/tela', obrigatorio: false, explicacao: 'Marcar libera todas as ações daquela tela (ou módulo) de uma vez. Um traço no meio indica que só algumas ações estão liberadas.' },
        { nome: 'Ler / Criar / Editar / Excluir', obrigatorio: false, explicacao: 'Cada ação pode ser liberada individualmente, tela por tela. As mudanças só valem depois de clicar em "Salvar permissões".' },
        { nome: 'Aplicar perfil', obrigatorio: false, explicacao: 'Substitui de uma vez todas as permissões do colaborador selecionado pelo perfil escolhido — use com cuidado, pois sobrescreve qualquer personalização feita manualmente antes.' },
        { nome: 'Duplicar de outro colaborador', obrigatorio: false, explicacao: 'Copia exatamente as mesmas permissões de outro colaborador já cadastrado — útil pra dar o mesmo acesso pra duas pessoas do mesmo cargo.' },
      ],
    },
  ],
  dicas: [
    'A senha temporária de um colaborador novo só é exibida uma vez, na hora da criação — anote antes de fechar o popup.',
    '"Excluir" apenas bloqueia o login e some com o colaborador da lista de Ativos; não apaga cadastro nem histórico. Use o filtro "Inativos" para encontrar e reativar quando precisar.',
    'Perfis de acesso ficam em Configurações → Perfis de Acesso — crie perfis novos lá pra reusar em vários colaboradores.',
    '"Nome completo" é o que aparece nas listas do sistema e na saudação do Dashboard; "Usuário" é o login — avise o colaborador se trocar o dele.',
    'Um colaborador já logado só vê o nome/usuário novo depois de sair e entrar de novo no sistema.',
  ],
};

const asLista = (data) => (Array.isArray(data) ? data : data?.results ?? []);

const GestaoUsuarios = () => {
  const [colaboradores, setColaboradores] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [valoresTela, setValoresTela] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [formNovo, setFormNovo] = useState({ nome: '', email: '', perfil: '' });
  const [modalSenha, setModalSenha] = useState(null);
  const [duplicarDeId, setDuplicarDeId] = useState('');
  const [nomeEdit, setNomeEdit] = useState('');
  const [usernameEdit, setUsernameEdit] = useState('');
  const [editando, setEditando] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState('ativos'); // 'ativos' | 'inativos'

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    setCarregando(true);
    try {
      const [resColab, resModulos, resPerfis] = await Promise.all([
        api.get('/configuracoes/perfis-acesso/'),
        api.get('/configuracoes/modulos/'),
        api.get('/configuracoes/perfis/'),
      ]);
      const listaColab = asLista(resColab.data);
      setColaboradores(listaColab);
      setModulos(asLista(resModulos.data));
      setPerfis(asLista(resPerfis.data));
      if (listaColab.length > 0) {
        selecionarColaborador(listaColab[0]);
      }
    } catch {
      setErro('Erro ao carregar usuários e permissões.');
    } finally {
      setCarregando(false);
    }
  };

  const permissoesTelaParaMapa = (permissoesTela, modulosLista) => {
    const mapa = {};
    modulosLista.forEach((m) => m.telas.forEach((t) => {
      const valor = permissoesTela?.[t.chave];
      if (valor) mapa[t.id] = valor;
    }));
    return mapa;
  };

  const selecionarColaborador = (colab) => {
    setSelecionado(colab);
    setValoresTela(permissoesTelaParaMapa(colab.permissoes_tela, modulos));
    setDuplicarDeId('');
    setNomeEdit(colab.nome_completo);
    setUsernameEdit(colab.username);
    setEditando(false);
  };

  const atualizarColaboradorLocal = (atualizado) => {
    setColaboradores((prev) => prev.map((c) => (c.id === atualizado.id ? atualizado : c)));
    setSelecionado(atualizado);
    setValoresTela(permissoesTelaParaMapa(atualizado.permissoes_tela, modulos));
    setNomeEdit(atualizado.nome_completo);
    setUsernameEdit(atualizado.username);
  };

  const cancelarEdicaoIdentificacao = () => {
    setNomeEdit(selecionado.nome_completo);
    setUsernameEdit(selecionado.username);
    setEditando(false);
  };

  const salvarIdentificacao = async () => {
    if (!selecionado) return;
    const dados = {};
    const nome = nomeEdit.trim();
    const username = usernameEdit.trim();
    if (nome && nome !== selecionado.nome_completo) dados.nome = nome;
    if (username && username !== selecionado.username) dados.username = username;
    if (Object.keys(dados).length === 0) return;

    setSalvando(true);
    setErro(''); setSucesso('');
    try {
      const res = await api.patch(`/configuracoes/perfis-acesso/${selecionado.id}/editar_identificacao/`, dados);
      atualizarColaboradorLocal(res.data);
      setEditando(false);
      setSucesso('Identificação atualizada com sucesso.');
    } catch (err) {
      const dadosErro = err.response?.data;
      setErro(
        dadosErro?.detail || dadosErro?.nome?.[0] || dadosErro?.username?.[0] ||
        dadosErro?.non_field_errors?.[0] || 'Erro ao atualizar identificação.'
      );
    } finally {
      setSalvando(false);
    }
  };

  const alterarValor = (telaId, acao, valor) => {
    setValoresTela((prev) => ({
      ...prev,
      [telaId]: { ...prev[telaId], [acao]: valor },
    }));
  };

  const aplicarPerfilPreset = async (perfilSlug) => {
    if (!selecionado) return;
    setSalvando(true);
    setErro(''); setSucesso('');
    try {
      const res = await api.post(`/configuracoes/perfis-acesso/${selecionado.id}/aplicar_perfil/`, { perfil: perfilSlug });
      atualizarColaboradorLocal(res.data);
      setSucesso('Perfil aplicado com sucesso.');
    } catch {
      setErro('Erro ao aplicar perfil.');
    } finally {
      setSalvando(false);
    }
  };

  const duplicarPermissoes = async () => {
    if (!selecionado || !duplicarDeId) return;
    setSalvando(true);
    setErro(''); setSucesso('');
    try {
      const res = await api.post(`/configuracoes/perfis-acesso/${selecionado.id}/duplicar_de/`, { origem_id: duplicarDeId });
      atualizarColaboradorLocal(res.data);
      setSucesso('Permissões duplicadas com sucesso.');
    } catch {
      setErro('Erro ao duplicar permissões.');
    } finally {
      setSalvando(false);
    }
  };

  const salvarPermissoes = async () => {
    if (!selecionado) return;
    setSalvando(true);
    setErro(''); setSucesso('');
    try {
      const permissoes = Object.entries(valoresTela).map(([telaId, v]) => ({
        tela_id: Number(telaId),
        leitura: !!v.leitura, gravacao: !!v.gravacao, edicao: !!v.edicao, exclusao: !!v.exclusao,
      }));
      const res = await api.post(`/configuracoes/perfis-acesso/${selecionado.id}/definir_permissoes_tela/`, { permissoes });
      atualizarColaboradorLocal(res.data);
      setSucesso('Permissões salvas com sucesso.');
    } catch {
      setErro('Erro ao salvar permissões.');
    } finally {
      setSalvando(false);
    }
  };

  const toggleAtivo = async (colab) => {
    setErro(''); setSucesso('');
    try {
      const res = await api.patch(`/configuracoes/perfis-acesso/${colab.id}/`, { ativo: !colab.ativo });
      atualizarColaboradorLocal(res.data);
    } catch {
      setErro('Erro ao atualizar status do colaborador.');
    }
  };

  const criarColaborador = async (e) => {
    e.preventDefault();
    setErro(''); setSucesso('');
    try {
      const res = await api.post('/configuracoes/perfis-acesso/criar_usuario/', formNovo);
      setModalNovo(false);
      setModalSenha({ username: res.data.username, senha: res.data.senha_temporaria });
      setFormNovo({ nome: '', email: '', perfil: '' });
      carregarTudo();
    } catch (err) {
      setErro(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Erro ao criar colaborador.');
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ color: VERDE }}>Usuários e Permissões</h1>
          <AjudaTela {...AJUDA} />
        </div>
        <button
          onClick={() => setModalNovo(true)}
          className="text-white px-4 py-2 rounded-lg font-medium"
          style={{ backgroundColor: VERDE }}
        >
          + Novo colaborador
        </button>
      </div>

      {erro && <div className="p-3 text-red-700 bg-red-50 border border-red-200 rounded-lg text-sm">{erro}</div>}
      {sucesso && <div className="p-3 text-green-700 bg-green-50 border border-green-200 rounded-lg text-sm">{sucesso}</div>}

      {carregando ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow lg:col-span-1">
            <div className="p-3 border-b font-medium text-sm flex items-center justify-between" style={{ color: VERDE }}>
              <span>Colaboradores</span>
              <div className="flex text-xs rounded-lg border overflow-hidden" style={{ borderColor: VERDE }}>
                <button
                  onClick={() => setFiltroAtivo('ativos')}
                  className="px-2 py-1 font-medium"
                  style={filtroAtivo === 'ativos' ? { backgroundColor: VERDE, color: '#fff' } : { color: VERDE }}
                >
                  Ativos
                </button>
                <button
                  onClick={() => setFiltroAtivo('inativos')}
                  className="px-2 py-1 font-medium"
                  style={filtroAtivo === 'inativos' ? { backgroundColor: VERDE, color: '#fff' } : { color: VERDE }}
                >
                  Inativos
                </button>
              </div>
            </div>
            <ul className="max-h-[600px] overflow-y-auto">
              {colaboradores.filter((c) => (filtroAtivo === 'ativos' ? c.ativo : !c.ativo)).map((c) => (
                <li
                  key={c.id}
                  onClick={() => selecionarColaborador(c)}
                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                    selecionado?.id === c.id ? 'bg-gray-50' : ''
                  }`}
                  style={selecionado?.id === c.id ? { borderLeft: `3px solid ${AZUL}` } : {}}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.nome_completo}</p>
                    <p className="text-xs text-gray-500">{c.perfil_acesso_nome}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    c.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {c.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </li>
              ))}
              {colaboradores.filter((c) => (filtroAtivo === 'ativos' ? c.ativo : !c.ativo)).length === 0 && (
                <li className="p-4 text-sm text-gray-500">
                  {filtroAtivo === 'ativos'
                    ? 'Nenhum colaborador ativo cadastrado ainda.'
                    : 'Nenhum colaborador inativo — ninguém foi excluído até agora.'}
                </li>
              )}
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow lg:col-span-2 p-4">
            {!selecionado ? (
              <p className="text-gray-500 text-sm">Selecione um colaborador para gerenciar as permissões.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  {!editando ? (
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">{selecionado.nome_completo}</h2>
                      <p className="text-xs text-gray-500">{selecionado.username} · {selecionado.email}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1 mr-3">
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase tracking-wide">Nome completo</label>
                        <input
                          autoFocus
                          className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                          value={nomeEdit}
                          onChange={(e) => setNomeEdit(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 uppercase tracking-wide">Usuário (login)</label>
                        <input
                          className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                          value={usernameEdit}
                          onChange={(e) => setUsernameEdit(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 shrink-0">
                    {!editando ? (
                      <button
                        onClick={() => setEditando(true)}
                        className="text-xs px-3 py-1 rounded-lg border font-medium"
                        style={{ borderColor: AZUL, color: AZUL }}
                      >
                        Editar
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={cancelarEdicaoIdentificacao}
                          className="text-xs px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={salvarIdentificacao}
                          disabled={
                            salvando ||
                            (nomeEdit.trim() === selecionado.nome_completo && usernameEdit.trim() === selecionado.username)
                          }
                          className="text-xs px-3 py-1 rounded-lg text-white font-medium disabled:opacity-50"
                          style={{ backgroundColor: AZUL }}
                        >
                          Salvar
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => toggleAtivo(selecionado)}
                      className="text-xs px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
                    >
                      {selecionado.ativo ? 'Excluir' : 'Reativar acesso'}
                    </button>
                  </div>
                </div>
                {editando && (
                  <p className="text-[11px] text-gray-400 -mt-2">
                    "Nome completo" é o que aparece nas listas e na saudação do Dashboard. Trocar o
                    "Usuário" muda o que essa pessoa digita pra entrar no sistema — avise antes de salvar.
                  </p>
                )}

                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Aplicar perfil (preset)</p>
                  <div className="flex flex-wrap gap-2">
                    {perfis.map((perfil) => (
                      <button
                        key={perfil.slug}
                        disabled={salvando}
                        onClick={() => aplicarPerfilPreset(perfil.slug)}
                        className="text-xs px-3 py-1.5 rounded-lg border font-medium disabled:opacity-50"
                        style={{ borderColor: AZUL, color: AZUL }}
                      >
                        {perfil.nome}
                      </button>
                    ))}
                    {perfis.length === 0 && (
                      <p className="text-xs text-gray-500">Nenhum perfil cadastrado ainda — crie um em Configurações → Perfis de Acesso.</p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Duplicar permissões de outro colaborador</p>
                  <div className="flex gap-2">
                    <select
                      value={duplicarDeId}
                      onChange={(e) => setDuplicarDeId(e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg p-2 text-sm"
                    >
                      <option value="">Selecione um colaborador...</option>
                      {colaboradores.filter((c) => c.ativo && c.id !== selecionado.id).map((c) => (
                        <option key={c.id} value={c.id}>{c.nome_completo}</option>
                      ))}
                    </select>
                    <button
                      onClick={duplicarPermissoes}
                      disabled={salvando || !duplicarDeId}
                      className="text-xs px-3 py-1.5 rounded-lg border font-medium disabled:opacity-50 whitespace-nowrap"
                      style={{ borderColor: VERDE, color: VERDE }}
                    >
                      Duplicar
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-600">Permissões por tela</p>
                  <TelaPermissoesGrid modulos={modulos} valores={valoresTela} onChange={alterarValor} />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={salvarPermissoes}
                    disabled={salvando}
                    className="text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                    style={{ backgroundColor: AZUL }}
                  >
                    {salvando ? 'Salvando...' : 'Salvar permissões'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {modalNovo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-lg font-bold mb-4" style={{ color: VERDE }}>Novo colaborador</h2>
            <form onSubmit={criarColaborador} className="space-y-3">
              <input
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="Nome completo *"
                required
                value={formNovo.nome}
                onChange={(e) => setFormNovo({ ...formNovo, nome: e.target.value })}
              />
              <input
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="E-mail *"
                type="email"
                required
                value={formNovo.email}
                onChange={(e) => setFormNovo({ ...formNovo, email: e.target.value })}
              />
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Perfil (opcional)</label>
                <select
                  className="w-full border rounded-lg p-2 text-sm"
                  value={formNovo.perfil}
                  onChange={(e) => setFormNovo({ ...formNovo, perfil: e.target.value })}
                >
                  <option value="">Sem perfil — definir manualmente depois</option>
                  {perfis.map((p) => <option key={p.slug} value={p.slug}>{p.nome}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalNovo(false)} className="flex-1 border border-gray-300 rounded-lg py-2 text-sm hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" className="flex-1 text-white rounded-lg py-2 text-sm" style={{ backgroundColor: VERDE }}>
                  Criar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalSenha && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h2 className="text-lg font-bold mb-2" style={{ color: VERDE }}>Colaborador criado!</h2>
            <p className="text-xs text-gray-500 mb-3">Anote a senha temporária abaixo — ela não será exibida novamente.</p>
            <div className="bg-gray-50 border rounded-lg p-3 text-sm space-y-1 mb-4">
              <p><span className="text-gray-500">Usuário:</span> <span className="font-mono">{modalSenha.username}</span></p>
              <p><span className="text-gray-500">Senha temporária:</span> <span className="font-mono font-bold">{modalSenha.senha}</span></p>
            </div>
            <button onClick={() => setModalSenha(null)} className="w-full text-white rounded-lg py-2 text-sm" style={{ backgroundColor: AZUL }}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoUsuarios;
